/* eslint-disable no-console */
import moment, { min, max } from 'moment';

import { MeteringMethodModel } from '../metering-method.model';
import { Commodity } from '../types/commodity';
import { GridConnection } from '../types/grid-connection.enum';
import { ToNominate } from '../types/to-nominate-type.enum';

import { ContractPerimeter } from './contract-perimeter.model';
import { Contract } from './contract.model';
import { Establishment } from './establishment.model';
import { Site } from './site.model';

export class PointType {
  description: string;
  code: string;

  constructor(attributes: Partial<PointType> = {}) {
    Object.assign(this, attributes);
  }
}

export class PointAdditionalInfo {
  messlokation?: string;

  constructor(attributes: Partial<PointAdditionalInfo> = {}) {
    Object.assign(this, attributes);
  }
}

/**
 * Model for API EndPoint Point Model
 */
export class DeliveryPoint {
  /**
   * Commodity associated to delivery point.
   */
  commodity: Commodity;

  /**
   * Metering point code.
   */
  meteringPoint: string;

  /**
   * Grid operator code.
   */
  gridOperator: string;

  /**
   * Site labels
   */
  siteLabels: {
    internal: string;
    external: string;
  };

  parentMeteringPoint: string;

  parentGridOperator: string;

  pointType: PointType;

  zone: string;

  country: string;

  siteId: number;

  siteName: string;

  site: Site;

  establishmentCode: number;

  validityStartDate: moment.Moment;

  validityEndDate: moment.Moment;

  contract: Contract;

  msbNumber: string;

  meteringPointOperator: string;

  marketArea: string;

  meteringMethod?: string;

  /**
   * Establishment linked to the delivery point
   */
  establishment: Establishment;

  perimeter?: ContractPerimeter;

  inDelivery: boolean;

  toNominate: ToNominate;

  bcvVolumeIdentifier: string;

  /**
   * A list a grid connection direction that are associated with the point through different perimeter
   * Needed this for point selector component
   */
  associatedGridConnections?: GridConnection[];

  accountId?: number;

  /**
   * Nomination Forecast
   */
  volumeUnit?: string;

  /**
   * Model containing additional information about the delivery point, like the messlokation for DE points.
   */
  addtionalInfo?: PointAdditionalInfo;

  constructor(attributes: Partial<DeliveryPoint> = {}) {
    Object.assign(this, attributes);
  }

  get isNominated() {
    return this.toNominate === ToNominate.mandatory || this.toNominate === ToNominate.indicative;
  }

  get encodedGridOperator() {
    return encodeURIComponent(this.gridOperator);
  }

  get encodedMeteringPoint() {
    return encodeURIComponent(this.meteringPoint);
  }

  toString() {
    return `${this.meteringPoint} ${this.gridOperator}`;
  }

  equalTo(deliveryPoint: DeliveryPoint): boolean {
    return this.poorlyEqualTo(deliveryPoint) && this.commodity === deliveryPoint.commodity;
  }

  searchOn(text: string): boolean {
    return (
      this.site.labels.internal.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
      this.meteringPoint.toLocaleLowerCase().startsWith(text.toLocaleLowerCase())
    );
  }

  /**
   * the key (meteringPoint, gridOperator) may be enough to be a primary key
   * when backend doesn't send commodity...
   */
  poorlyEqualTo(deliveryPoint: DeliveryPoint): boolean {
    return this.meteringPoint === deliveryPoint.meteringPoint && this.gridOperator === deliveryPoint.gridOperator;
  }

  hydrateFromSites(sites: Site[] | Site) {
    const s = Array.isArray(sites) ? sites : [sites];
    s.some((site: Site) => {
      const found = site.points.find((deliveryPoint: DeliveryPoint) => deliveryPoint.poorlyEqualTo(this));
      if (found) {
        // deliveryPoint from Site.point is much more complete (but may lack some info present in current instance, like contract)
        // So we merge in both side
        // first add these 3 properties to found in order to keep them in when merging
        found.addtionalInfo = this.addtionalInfo;
        found.msbNumber = this.msbNumber;
        found.meteringPointOperator = this.meteringPointOperator;
        found.marketArea = this.marketArea;
        found.country = this.country ?? found.country;
        found.validityStartDate = this.validityStartDate;
        found.validityEndDate = this.validityEndDate;
        Object.assign(this, found);
        Object.assign(found, this);
        this.site = site;
        this.siteId = site.id;
        return true;
      }

      return false;
    });
  }

  isActive(): boolean {
    // Points in the future are considered as active
    return !this.validityEndDate || moment().isSameOrBefore(this.validityEndDate);
  }

  extendValidity(point: DeliveryPoint) {
    if (this.validityRangeNoOverlaps(point)) {
      console.warn('Impossible to extend validity range of', this, 'with', point);
      return;
    }

    if (this.validityStartDate) {
      this.validityStartDate = point.validityStartDate ? min(this.validityStartDate, point.validityStartDate) : undefined;
    }

    if (this.validityEndDate) {
      this.validityEndDate = point.validityEndDate ? max(this.validityEndDate, point.validityEndDate) : undefined;
    }
    this.inDelivery = this.inDelivery || point.inDelivery;
  }

  validityRangeNoOverlaps(point: DeliveryPoint): boolean {
    return (
      (this.validityEndDate && point.validityStartDate && this.validityEndDate.isBefore(point.validityStartDate)) ||
      (point.validityEndDate && this.validityStartDate && point.validityEndDate.isBefore(this.validityStartDate))
    );
  }

  validityRangeOVerlapsWithContractDates(contractStartDate: moment.Moment, contractEndDate: moment.Moment): boolean {
    if (!this.validityEndDate && this.validityStartDate) return this.validityStartDate.isSameOrBefore(contractEndDate);
    if (this.validityStartDate && contractStartDate && this.validityStartDate.isSameOrBefore(contractStartDate))
      return contractStartDate.isSameOrBefore(this.validityEndDate);
    return this.validityEndDate ? this.validityEndDate.isSameOrBefore(contractEndDate) : false;
  }

  /**
   * Get the site, given a delivery point
   */
  getSite(sites: Site[]): Site | undefined {
    return sites
      ?.filter((site: Site) => {
        const correspondingPoints = site.points.find((p) => p.poorlyEqualTo(this));
        return correspondingPoints
          ? correspondingPoints.validityRangeOVerlapsWithContractDates(this.contract.startDate, this.contract.endDate)
          : false;
      })
      ?.sort((a: Site, b: Site) =>
        b.points
          .find((point) => point.poorlyEqualTo(this))
          .validityStartDate.diff(a.points.find((point) => point.poorlyEqualTo(this)).validityStartDate),
      )
      ?.find((site: Site) => site.containsDeliveryPoint(this));
  }

  assignSite(sites: Site[]) {
    const site = this.getSite(sites);
    if (site) {
      this.site = site;
      this.siteLabels = site.labels;
    }
    return this;
  }

  getMeteringMethod(meteringMethods: MeteringMethodModel[] = []): string {
    const endDate = this.validityEndDate ?? moment();
    const meteringItem = meteringMethods
      .find((mm) => mm.meteringPointCode === this.meteringPoint)
      ?.items?.find((item) => endDate.isSameOrAfter(item.fromDate) && endDate.isSameOrBefore(item.endDate));
    return meteringItem ? meteringItem.mappedMeteringMethod : undefined;
  }

  forecastKey(direction: GridConnection) {
    return `${this.meteringPoint}-${direction}`;
  }
}
